/**
 * Client-side helper that syncs an authenticated user's ad_storage consent
 * choice to the backend so server-side Reddit CAPI event gating stays in
 * step with what the user sees in the banner.
 *
 * Fire-and-forget semantics: UI callers should NOT await this promise on
 * their critical path. The banner persists the cookie (the source of truth
 * for GA) synchronously; this relay is only a best-effort server update.
 *
 * Anonymous visitors: skip. There is no user document to attribute consent
 * to, and no Reddit CAPI event can fire for an anonymous user anyway. The
 * consent state lives in the `nrs_ga_consent` cookie and is forwarded to
 * Composer at signup time as part of the create-user request body.
 */

import type { StoredConsentChoice } from "../analytics";

const CONSENT_ENDPOINT = "/api/reddit/consent";

/**
 * POSTs the user's consent choice to the local relay route, which forwards
 * to Composer. Swallows transport / HTTP errors: a failed relay must not
 * block banner UX or trigger visible errors.
 */
export async function relayRedditAdConsent(
  choice: StoredConsentChoice,
  accessToken: string | null | undefined
): Promise<void> {
  if (!accessToken || typeof accessToken !== "string") return;
  if (typeof fetch === "undefined") return;
  try {
    await fetch(CONSENT_ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
      },
      body: JSON.stringify({ choice }),
      keepalive: true,
    });
  } catch {
    /* @architecture:acceptable - best-effort consent relay */
  }
}
